<template>
  <div class="user-cards">
    <!-- Toolbar -->
    <table-toolbar
      v-if="showToolbar"
      :show-title="showHeader"
      :title="title"
      :show-count="true"
      :current-count="users.filtered.length"
      :total-count="totalAllowedUsers > 0 ? totalAllowedUsers : users.all.length"
      :searchable="showSearch"
      :search-query="searchQuery"
      @search-change="searchQuery = $event"
      :show-per-page-selector="false"
      :show-refresh="false"
      :show-column-visibility="viewMode === 'list'"
      :columns="availableTableColumns"
      :visible-columns="visibleColumns"
      @column-toggle="handleColumnVisibilityChange"
      :show-view-toggle="showViewToggle"
      :view-mode="viewMode"
      @view-mode-change="viewMode = $event"
      :disabled="loading"
    >
      <template #action-buttons>
        <themed-button
          v-if="$gate.isAdmin() || $gate.isClientAdmin()"
          @click="handleAddEntity('users', 'manage-users')"
          variant="default"
        >
          <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
          <span>Manage Users</span>
        </themed-button>
      </template>
    </table-toolbar>

    <!-- Grid View -->
    <themed-grid
      v-if="viewMode === 'grid'"
      :data="filteredUsers"
      :fields="gridFields"
      :columns="3"
      :clickable="false"
      :loading="loading"
      empty-message="No users found"
      empty-icon="user-icon"
      item-key="id"
    >
      <template #actions="{ item }">
        <div class="user-cards__actions">
          <themed-button
            v-if="$gate.can('update', 'user')"
            variant="ghost"
            size="sm"
            @click.stop="editUser(item.id)"
            aria-label="Edit User"
          >
            <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
            <span>Edit</span>
          </themed-button>
          
          <themed-button
            v-if="$gate.can('delete', 'user')"
            variant="destructive"
            size="sm"
            @click.stop="deleteUser(item.id)"
            aria-label="Delete User"
          >
            <theme-icon icon="trash-icon" type="icons" classes="w-4 h-4" />
            <span>Delete</span>
          </themed-button>
        </div>
      </template>
    </themed-grid>

    <!-- List View -->
    <themed-table
      v-else
      :data="filteredUsers"
      :columns="visibleTableColumns"
      :hoverable="true"
      :searchable="false"
      :column-visibility="false"
      empty-message="No users found"
      empty-icon="user-icon"
      @action="handleAction"
      @row-click="handleRowClick"
    />

    <!-- Manage Users Modal -->
    <manage-users
      :client-id="clients.selectedClient?.id"
      :user-id="selectedUserId"
      @closed="onModalClosed"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import ThemedTable from '../ThemedTable.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';
import ManageUsers from './ManageUsers.vue';

export default {
  name: 'UserCards',

  components: {
    ThemedTable,
    ThemedGrid,
    ThemedButton,
    ThemeIcon,
    TableToolbar,
    ManageUsers
  },

  props: {
    userData: {
      type: Array,
      required: true
    },

    showHeader: {
      type: Boolean,
      default: true
    },

    smallHeader: {
      type: Boolean,
      default: false
    },

    showFilters: {
      type: Boolean,
      default: true
    },

    showToolbar: {
      type: Boolean,
      default: true
    },

    showSearch: {
      type: Boolean,
      default: true
    },

    showViewToggle: {
      type: Boolean,
      default: true
    },

    view: {
      type: String,
      default: 'grid'
    },

    title: {
      type: String,
      default: 'Users'
    }
  },

  data() {
    return {
      selectedUserId: null,
      viewMode: this.view,
      searchQuery: '',
      loading: false,
      loadingUserId: null,
      visibleColumns: []
    };
  },

  computed: {
    ...mapState(['clients']),
    ...mapState(['users']),

    totalAllowedUsers() {
      const moduleData = this.$store.getters['moduleAccess/getModuleBySlug']('users');
      return moduleData?.subscription_details?.quantity || 0;
    },

    selectedUser() {
      return _.find(this.users.all, ['id', this.selectedUserId]) || {};
    },

    filteredUsers() {
      let filtered = this.users.filtered;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(user => {
          const name = user.name || '';
          const email = user.email || '';
          const department = user.department || '';
          return name.toLowerCase().includes(query) ||
                 email.toLowerCase().includes(query) ||
                 department.toLowerCase().includes(query);
        });
      }

      return filtered;
    },

    gridFields() {
      const isAdmin = this.$gate.isAdmin() || this.$gate.isClientAdmin();
      
      const fields = [
        {
          field: 'name',
          type: 'title'
        },
        {
          field: 'role',
          type: 'subtitle',
          transform: (value) => value?.label
        },
        {
          field: 'department',
          label: 'DEPARTMENT',
          type: 'text'
        },
        {
          field: 'title',
          label: 'TITLE',
          type: 'text'
        },
        {
          field: 'cell',
          label: 'CELL',
          type: 'text',
          icon: 'phone-icon'
        },
        {
          field: 'email',
          label: 'EMAIL',
          type: 'email',
          icon: 'email-icon'
        }
      ];
      
      // Add 2FA badge in content area for admins
      if (isAdmin) {
        fields.push({
          field: 'two_fa_status',
          label: '2FA',
          showLabel: false,
          type: 'text',
          badge: true,
          transform: (value, item) => {
            // Use the two_fa_status from backend, or fallback to computed value
            return value || (item.is_2fa_enabled ? 'Enabled' : 'Disabled');
          },
          classFunction: (item) => {
            if (item.is_2fa_enabled && item.two_fa_method) {
              return 'badge--green';
            } else if (item.is_2fa_enabled && !item.two_fa_method) {
              return 'badge--yellow';
            }
            return 'badge--red';
          }
        });
      }
      
      return fields;
    },

    tableColumns() {
      return [
        {
          field: 'name',
          label: 'Name',
          type: 'text',
          sortable: true,
          showInTable: true,
          alwaysVisible: true
        },
        {
          field: 'role',
          label: 'Role',
          type: 'text',
          sortable: true,
          showInTable: true,
          transform: (value) => value?.label
        },
        {
          field: 'department',
          label: 'Department',
          type: 'text',
          sortable: true,
          showInTable: true
        },
        {
          field: 'title',
          label: 'Title',
          type: 'text',
          sortable: true,
          showInTable: true
        },
        {
          field: 'cell',
          label: 'Cell',
          type: 'text',
          sortable: true,
          showInTable: true
        },
        {
          field: 'phone',
          label: 'Office',
          type: 'text',
          sortable: true,
          showInTable: true,
          transform: (value, row) => {
            if (!row || !value) return '';
            return row.extension ? `${value} x${row.extension}` : value;
          }
        },
        {
          field: 'email',
          label: 'Email',
          type: 'email',
          sortable: true,
          showInTable: true
        },
        {
          field: 'two_fa_status',
          label: 'Authentication',
          type: 'badge',
          sortable: true,
          showInTable: this.$gate.isAdmin() || this.$gate.isClientAdmin(),
          badgeClass: (value, row) => this.get2FABadgeClass(row)
        },
        {
          field: 'actions',
          label: '',
          type: 'actions',
          width: '80px',
          align: 'right',
          showInTable: this.$gate.can('update', 'user') || this.$gate.can('delete', 'user'),
          alwaysVisible: true,
          buttons: [
            {
              name: 'edit',
              icon: 'edit-icon',
              variant: 'ghost',
              size: 'sm',
              iconOnly: true,
              visible: () => this.$gate.can('update', 'user')
            },
            {
              name: 'delete',
              icon: 'trash-icon',
              variant: 'destructive',
              size: 'sm',
              iconOnly: true,
              visible: () => this.$gate.can('delete', 'user')
            }
          ]
        }
      ];
    },

    availableTableColumns() {
      return this.tableColumns.filter(col => col.showInTable !== false);
    },

    visibleTableColumns() {
      return this.tableColumns.filter(col => {
        if (col.alwaysVisible) return true;
        return this.visibleColumns.includes(col.field);
      });
    }
  },

  watch: {
    userData: {
      immediate: true,
      handler(newData) {
        if (newData && newData.length > 0) {
          this.users.all = newData;
          this.users.filtered = newData;
          this.$emit('filterRequest');
        }
      }
    },

    viewMode(newValue) {
      localStorage.setItem('reopt_users_layout', newValue);
    }
  },

  mounted() {
    if (localStorage.getItem('reopt_users_layout')) {
      this.viewMode = localStorage.getItem('reopt_users_layout');
    }

    // Initialize visible columns
    const savedColumns = localStorage.getItem('users_table_visible_columns');
    if (savedColumns) {
      try {
        this.visibleColumns = JSON.parse(savedColumns);
      } catch (e) {
        this.initializeDefaultColumns();
      }
    } else {
      this.initializeDefaultColumns();
    }
    
    // Listen for user deletion from ManageUsers modal
    this.$eventHub.$on('user-deleted', this.handleUserDeleted);
  },
  
  beforeDestroy() {
    this.$eventHub.$off('user-deleted', this.handleUserDeleted);
  },

  methods: {
    initializeDefaultColumns() {
      this.visibleColumns = this.tableColumns
        .filter(col => col.showInTable !== false && !col.alwaysVisible)
        .map(col => col.field);
    },

    handleColumnVisibilityChange(columns) {
      this.visibleColumns = columns;
      localStorage.setItem('users_table_visible_columns', JSON.stringify(columns));
    },

    onModalClosed() {
      const index = this.users.all.findIndex(user => user.id === 'new');
      if (index !== -1) {
        this.users.all.splice(index, 1);
      }
      this.selectedUserId = null;
      this.users.selectedUser = null;
    },

    editUser(id) {
      this.selectedUserId = id;
      const user = _.find(this.users.all, ['id', id]);
      if (user) {
        this.users.selectedUser = user;
      }
      this.$modal.show('manage-users');
    },

    handleAction(data) {
      const actionName = data.action || data.name || data;
      const row = data.row || data.item;
      
      if (actionName === 'edit') {
        this.editUser(row.id);
      } else if (actionName === 'delete') {
        this.deleteUser(row.id);
      }
    },

    handleRowClick(row) {
      if (this.$gate.can('update', 'user')) {
        this.editUser(row.id);
      }
    },

    deleteUser(userId) {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete this user?',
        type: 'warning',
        buttonsStyling: false,
        confirmButtonText: 'Yes - Delete it',
        confirmButtonClass: 'btn btn-red',
        showCancelButton: true,
        cancelButtonClass: 'btn btn-secondary mr-4',
        reverseButtons: true
      }).then((result) => {
        if (result.value) {
          this.performDeleteUser(userId);
        }
      });
    },

    performDeleteUser(userId) {
      this.loadingUserId = userId;

      axios.delete(`/users/${userId}`)
        .then(() => {
          // Remove user from the lists
          this.users.all = this.users.all.filter(user => user.id !== userId);
          this.users.filtered = this.users.filtered.filter(user => user.id !== userId);

          this.$notify({
            type: 'success',
            title: 'Success',
            text: 'User deleted successfully'
          });
        })
        .catch((error) => {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: error.response?.data?.message || 'Failed to delete user'
          });
        })
        .finally(() => {
          this.loadingUserId = null;
        });
    },

    get2FABadgeClass(user) {
      if (user.is_2fa_enabled && user.two_fa_method) {
        return 'badge--green';
      } else if (user.is_2fa_enabled && !user.two_fa_method) {
        return 'badge--yellow';
      }
      return 'badge--red';
    },

    handleAddEntity(moduleSlug, newEntityModalName) {
      this.$moduleAccessOverlay.show(moduleSlug, {
        targetModal: newEntityModalName,
        forceUpdate: true,
        isThemedModal: true
      });
    },
    
    handleUserDeleted(userId) {
      // Remove user from the lists when deleted from ManageUsers modal
      this.users.all = this.users.all.filter(user => user.id !== userId);
      this.users.filtered = this.users.filtered.filter(user => user.id !== userId);
    }
  }
};
</script>
